import type { AppWorkerClient } from '../data/AppWorkerClient'
import type {
  ArAnalysisResponse,
  MetricComparisonKind,
  QuickBooksArTrendPeriod,
  QuickBooksCfoStatementsRenderContractResponse,
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
  QuickBooksGlSummaryResponse,
  RatioCard,
  ServerApiClient,
} from '../data/ServerApiClient'
import {
  CFO_DRILLDOWN_MANIFEST,
  CFO_DRILLDOWN_RECIPES_BY_ID,
  CFO_DRILLDOWN_VIEWS_BY_ID,
  findCfoDrilldownRecipeForMetric,
  normalizeCfoDrilldownMetricId,
  requiredCfoDrilldownTablesForRecipe,
  type CfoDrilldownRecipeManifest,
} from './cfoDrilldownManifest'
import type {
  CfoDrilldownArtifactReadiness,
  CfoDrilldownChartSpec,
  CfoDrilldownRecipeResult,
} from './cfoDrilldownTypes'

const trimStr = (value: unknown): string => String(value ?? '').trim()

const COMPARISON_KINDS: ReadonlySet<MetricComparisonKind> = new Set([
  'mom',
  'qoq',
  'yoy',
  'ytd',
  'l3m_vs_prior3m',
  'l12m_vs_prior12m',
])

const COMPARISON_ALIASES: Record<string, MetricComparisonKind> = {
  month_over_month: 'mom',
  prior_month: 'mom',
  mom: 'mom',
  quarter_over_quarter: 'qoq',
  prior_quarter: 'qoq',
  qoq: 'qoq',
  year_over_year: 'yoy',
  prior_year: 'yoy',
  yoy: 'yoy',
  year_to_date: 'ytd',
  ytd: 'ytd',
  last_3_months: 'l3m_vs_prior3m',
  last_3mo: 'l3m_vs_prior3m',
  l3m: 'l3m_vs_prior3m',
  l3m_vs_prior3m: 'l3m_vs_prior3m',
  last_12_months: 'l12m_vs_prior12m',
  last_12mo: 'l12m_vs_prior12m',
  l12m: 'l12m_vs_prior12m',
  l12m_vs_prior12m: 'l12m_vs_prior12m',
}

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map((item) => trimStr(item)).filter(Boolean)
  const single = trimStr(value)
  return single ? [single] : []
}

export const normalizeCfoDrilldownComparisonKind = (value: unknown): MetricComparisonKind | null => {
  const text = trimStr(value).toLowerCase()
  if (!text) return null
  if (COMPARISON_KINDS.has(text as MetricComparisonKind)) return text as MetricComparisonKind
  return COMPARISON_ALIASES[text] || null
}

export const requestedCfoDrilldownComparisonKind = (
  args: Record<string, any>,
  fallback: MetricComparisonKind = 'mom',
): MetricComparisonKind => {
  const tokens = [
    ...toStringArray(args.comparison ?? args.comparison_kind ?? args.comparisonKind),
    ...toStringArray(args.kind),
    ...toStringArray(args.kinds),
    ...toStringArray(args.comparisons),
  ]
  for (const token of tokens) {
    const kind = normalizeCfoDrilldownComparisonKind(token)
    if (kind) return kind
  }
  return fallback
}

const num = (value: unknown, fallback = 0): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const normalizeMonthStart = (value: unknown): string => {
  const text = trimStr(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return `${text.slice(0, 7)}-01`
  if (/^\d{4}-\d{2}$/.test(text)) return `${text}-01`
  const parsed = new Date(text)
  if (Number.isNaN(parsed.getTime())) return ''
  return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}-01`
}

const addMonths = (monthStart: string, delta: number): string => {
  const parsed = new Date(`${normalizeMonthStart(monthStart)}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return ''
  parsed.setUTCMonth(parsed.getUTCMonth() + delta)
  return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}-01`
}

export type CfoDrilldownWindow = {
  currentStart: string
  currentEndExclusive: string
  priorStart: string
  priorEndExclusive: string
  label: string
  shortLabel: string
}

export const buildCfoDrilldownWindow = (
  asOf: string,
  comparisonKind: MetricComparisonKind,
): CfoDrilldownWindow | null => {
  const currentMonth = normalizeMonthStart(asOf)
  if (!currentMonth) return null
  switch (comparisonKind) {
    case 'qoq': {
      const month = Number(currentMonth.slice(5, 7))
      const monthsIntoQuarter = ((month - 1) % 3) + 1
      const quarterEnd = addMonths(currentMonth, 3 - monthsIntoQuarter)
      return {
        currentStart: addMonths(quarterEnd, -2),
        currentEndExclusive: addMonths(quarterEnd, 1),
        priorStart: addMonths(quarterEnd, -5),
        priorEndExclusive: addMonths(quarterEnd, -2),
        label: 'Quarter over Quarter',
        shortLabel: 'QoQ',
      }
    }
    case 'yoy':
      return {
        currentStart: currentMonth,
        currentEndExclusive: addMonths(currentMonth, 1),
        priorStart: addMonths(currentMonth, -12),
        priorEndExclusive: addMonths(currentMonth, -11),
        label: 'Year over Year',
        shortLabel: 'YoY',
      }
    case 'ytd': {
      const year = Number(currentMonth.slice(0, 4))
      const currentStart = `${year}-01-01`
      return {
        currentStart,
        currentEndExclusive: addMonths(currentMonth, 1),
        priorStart: `${year - 1}-01-01`,
        priorEndExclusive: addMonths(currentMonth, -11),
        label: 'Year to Date',
        shortLabel: 'YTD',
      }
    }
    case 'l3m_vs_prior3m':
      return {
        currentStart: addMonths(currentMonth, -2),
        currentEndExclusive: addMonths(currentMonth, 1),
        priorStart: addMonths(currentMonth, -5),
        priorEndExclusive: addMonths(currentMonth, -2),
        label: 'Last 3 vs Prior 3 Months',
        shortLabel: 'Last 3mo',
      }
    case 'l12m_vs_prior12m':
      return {
        currentStart: addMonths(currentMonth, -11),
        currentEndExclusive: addMonths(currentMonth, 1),
        priorStart: addMonths(currentMonth, -23),
        priorEndExclusive: addMonths(currentMonth, -11),
        label: 'Last 12 vs Prior 12 Months',
        shortLabel: 'Last 12mo',
      }
    case 'mom':
    default:
      return {
        currentStart: currentMonth,
        currentEndExclusive: addMonths(currentMonth, 1),
        priorStart: addMonths(currentMonth, -1),
        priorEndExclusive: currentMonth,
        label: 'Month over Month',
        shortLabel: 'MoM',
      }
  }
}

const quoteDuckIdent = (value: string): string => `"${String(value).replace(/"/g, '""')}"`
const quoteDuckString = (value: string): string => `'${String(value).replace(/'/g, "''")}'`

const previewRowsAsObjects = (result: any): Array<Record<string, any>> => {
  const headers: string[] = Array.isArray(result?.headers) ? result.headers.map((h: any) => String(h || '')) : []
  const rows = Array.isArray(result?.preview_rows) ? result.preview_rows : (Array.isArray(result?.rows) ? result.rows : [])
  if (!headers.length) {
    return rows.map((row: any) => (row && typeof row === 'object' && !Array.isArray(row)) ? row : {})
  }
  return rows.map((row: any) => {
    if (row && typeof row === 'object' && !Array.isArray(row)) return row
    const values = Array.isArray(row) ? row : []
    const out: Record<string, any> = {}
    headers.forEach((header, idx) => {
      out[header] = values[idx]
    })
    return out
  })
}

const runDuckRows = async (
  workerClient: AppWorkerClient,
  sql: string,
  opts: { previewRowLimit?: number; timeoutMs?: number; caller: string },
  duckDbOpts?: Record<string, any> | null,
): Promise<Array<Record<string, any>>> => {
  const result = await workerClient.duckdb_run_sql(sql, {
    ...(duckDbOpts || {}),
    previewRowLimit: opts.previewRowLimit ?? 1000,
    timeoutMs: opts.timeoutMs ?? 60000,
    allowMutating: false,
    cacheExtracts: true,
    caller: opts.caller.startsWith('QuickBooksCfoCore:')
      ? opts.caller
      : `QuickBooksCfoCore:${opts.caller}`,
  })
  return previewRowsAsObjects(result)
}

const duckTableExists = async (
  workerClient: AppWorkerClient,
  tableName: string,
  duckDbOpts?: Record<string, any> | null,
): Promise<boolean> => {
  const rows = await runDuckRows(
    workerClient,
    `
WITH local_objects AS (
  SELECT table_name FROM information_schema.tables
  UNION ALL
  SELECT table_name FROM information_schema.views
)
SELECT COUNT(*) AS n
FROM local_objects
WHERE lower(table_name) = lower(${quoteDuckString(tableName)})`,
    { previewRowLimit: 1, timeoutMs: 15000, caller: `CfoDrilldownRecipes:table_exists:${tableName}` },
    duckDbOpts,
  )
  return num(rows[0]?.n) > 0
}

const tableRowCount = async (
  workerClient: AppWorkerClient,
  tableName: string,
  duckDbOpts?: Record<string, any> | null,
): Promise<number | null> => {
  const rows = await runDuckRows(
    workerClient,
    `SELECT COUNT(*) AS row_count FROM ${quoteDuckIdent(tableName)}`,
    { previewRowLimit: 1, timeoutMs: 30000, caller: `CfoDrilldownRecipes:row_count:${tableName}` },
    duckDbOpts,
  ).catch(() => [])
  const count = Number(rows[0]?.row_count)
  return Number.isFinite(count) ? count : null
}

export const buildCfoDrilldownArtifactReadiness = async (
  workerClient: AppWorkerClient | null | undefined,
  duckDbOpts?: Record<string, any> | null,
): Promise<CfoDrilldownArtifactReadiness[]> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') {
    return CFO_DRILLDOWN_MANIFEST.views.map((view) => ({
      view_id: view.id,
      table: view.table,
      label: view.label,
      status: 'missing',
      row_count: null,
      required_columns: view.required_columns,
      source_artifacts: view.source_artifacts || [],
    }))
  }
  const readiness: CfoDrilldownArtifactReadiness[] = []
  for (const view of CFO_DRILLDOWN_MANIFEST.views) {
    const exists = await duckTableExists(workerClient, view.table, duckDbOpts)
    readiness.push({
      view_id: view.id,
      table: view.table,
      label: view.label,
      status: exists ? 'ready' : 'missing',
      row_count: exists ? await tableRowCount(workerClient, view.table, duckDbOpts) : null,
      required_columns: view.required_columns,
      source_artifacts: view.source_artifacts || [],
    })
  }
  return readiness
}

// Governed server tools that still work when local DuckDB is unavailable, so a
// not-ready recipe never dead-ends the agent into restating the visible card.
const CFO_DRILLDOWN_FALLBACK_TOOLS = [
  'get_cfo_metric_evidence',
  'get_cfo_page_contract',
  'search_cfo_gl_summary',
] as const

const CFO_DRILLDOWN_FALLBACK_HINT =
  'Drilldown evidence is unavailable from this recipe. Do NOT answer with a restatement of what the user already sees. ' +
  'Instead call get_cfo_metric_evidence for this metric (or get_cfo_page_contract / search_cfo_gl_summary) and give a grounded read from that governed server evidence.'

const recipeNotReady = (
  recipe: CfoDrilldownRecipeManifest | null,
  summary: string,
  missingTables: string[],
  artifactReadiness: CfoDrilldownArtifactReadiness[] = [],
): CfoDrilldownRecipeResult => ({
  status: 'not_ready',
  recipe_id: recipe?.id || null,
  recipe_label: recipe?.label || null,
  manifest_version: CFO_DRILLDOWN_MANIFEST.version,
  summary,
  missing_tables: missingTables,
  semantic_gap: {
    required: missingTables,
    message: 'Run CFO Home warmup or Update QuickBooks data so FuseGrid can build canonical local AI analytics views.',
  },
  freshness: { source: 'local_duckdb', required_tables: missingTables, artifact_readiness: artifactReadiness },
  fallback_policy: {
    custom_sql_allowed: Boolean(recipe?.supports_custom_sql_fallback),
    custom_sql_scope: 'manifest_views_only',
  },
  fallback_tools: [...CFO_DRILLDOWN_FALLBACK_TOOLS],
  next_step_hint: CFO_DRILLDOWN_FALLBACK_HINT,
  actions: [
    {
      label: 'Refresh CFO analytics',
      prompt: 'Check whether the local CFO analytics artifacts are ready, then rerun this drilldown.',
    },
  ],
})

const ensureRecipeTablesReady = async (
  workerClient: AppWorkerClient,
  duckDbOpts: Record<string, any> | null | undefined,
  recipe: CfoDrilldownRecipeManifest,
): Promise<{ missing: string[]; artifactReadiness: CfoDrilldownArtifactReadiness[] }> => {
  const artifactReadiness = await buildCfoDrilldownArtifactReadiness(workerClient, duckDbOpts)
  const statusByTable = new Map(artifactReadiness.map((item) => [item.table.toLowerCase(), item.status]))
  const missing = requiredCfoDrilldownTablesForRecipe(recipe)
    .filter((table) => statusByTable.get(table.toLowerCase()) !== 'ready')
  return { missing, artifactReadiness }
}

const statementMetricFilterSql = (metricId: string): string => {
  const metric = normalizeCfoDrilldownMetricId(metricId)
  if (metric === 'revenue') {
    return `(
      lower(coalesce(account_class, '')) IN ('revenue', 'income')
      OR lower(coalesce(account_type, '')) LIKE '%income%'
      OR lower(coalesce(account_type, '')) LIKE '%revenue%'
    )
    AND lower(coalesce(account_type, '')) NOT LIKE '%cost of goods%'
    AND lower(coalesce(account_type, '')) NOT LIKE '%cogs%'
    AND lower(coalesce(account_breadcrumb, '')) NOT LIKE '%cost of goods%'
    AND lower(coalesce(account_breadcrumb, '')) NOT LIKE '%cogs%'`
  }
  if (metric === 'cost_of_sales') {
    return `(
      lower(coalesce(account_type, '')) LIKE '%cost of goods%'
      OR lower(coalesce(account_type, '')) LIKE '%cogs%'
      OR lower(coalesce(account_breadcrumb, '')) LIKE '%cost of goods%'
      OR lower(coalesce(account_breadcrumb, '')) LIKE '%cogs%'
    )`
  }
  if (metric === 'operating_expenses') {
    return `(
      lower(coalesce(account_class, '')) = 'expense'
      OR lower(coalesce(account_type, '')) LIKE '%expense%'
      OR lower(coalesce(account_type, '')) = 'other expense'
    )
    AND lower(coalesce(account_type, '')) NOT LIKE '%cost of goods%'
    AND lower(coalesce(account_type, '')) NOT LIKE '%cogs%'
    AND lower(coalesce(account_breadcrumb, '')) NOT LIKE '%cost of goods%'
    AND lower(coalesce(account_breadcrumb, '')) NOT LIKE '%cogs%'`
  }
  if (metric === 'cash' || metric === 'cash_on_hand' || metric === 'net_cash') {
    return `(
      lower(coalesce(statement_type, '')) LIKE '%balance%'
      OR lower(coalesce(account_class, '')) IN ('asset', 'assets')
      OR lower(coalesce(account_type, '')) LIKE '%asset%'
      OR lower(coalesce(account_breadcrumb, '')) LIKE '%asset%'
    )
    AND (
      lower(coalesce(account_type, '')) LIKE '%cash%'
      OR lower(coalesce(account_label, '')) LIKE '%cash%'
      OR lower(coalesce(account_breadcrumb, '')) LIKE '%cash%'
      OR lower(coalesce(account_label, '')) LIKE '%bank%'
      OR lower(coalesce(account_breadcrumb, '')) LIKE '%bank%'
    )`
  }
  return 'true'
}

const isExpenseLikeMetricId = (metricId: string): boolean => {
  const metric = normalizeCfoDrilldownMetricId(metricId)
  return metric === 'cost_of_sales' || metric === 'operating_expenses'
}

const incomeNetIncomeEffect = (lineDelta: number, currentValue: number, priorValue: number): number =>
  currentValue < 0 || priorValue < 0 ? -lineDelta : lineDelta

const expenseNetIncomeEffect = (lineDelta: number): number => -lineDelta

const metricLabelFor = (metricId: string, fallback: string): string => {
  switch (normalizeCfoDrilldownMetricId(metricId)) {
    case 'cost_of_sales':
      return 'Cost of Goods Sold'
    case 'operating_expenses':
      return 'Operating Expenses'
    case 'revenue':
      return 'Revenue'
    case 'cash':
    case 'cash_on_hand':
    case 'net_cash':
      return 'Cash'
    case 'accounts_receivable':
      return 'Accounts Receivable'
    case 'days_sales_outstanding':
      return 'Days Sales Outstanding'
    case 'net_income':
      return 'Net Income'
    default:
      return fallback || metricId || 'Selected metric'
  }
}

// --- Governed server statement recipes ---------------------------------------
//
// The statement recipes were originally local-DuckDB-only, which dead-ends on
// lightweight CFO pages (Home never boots the app worker) and anywhere the
// warm-artifact pipeline is disabled. The governed statements-render contract
// (`getQuickBooksCfoStatementsRender`) carries the same information — P&L /
// balance-sheet leaf rows with QBO account types and per-month values — so
// these builders serve the identical recipe result shape from the server when
// the local path is unavailable or its semantic views are not warmed.

export type CfoDrilldownServerContext = {
  appApi: Pick<ServerApiClient, 'getQuickBooksRatios'> &
    Partial<
      Pick<
        ServerApiClient,
        'getQuickBooksCfoStatementsRender' | 'getQuickBooksArAnalysis' | 'getQuickBooksGlSummary'
      >
    >
  workbookId: string | null | undefined
}

const SERVER_STATEMENT_RECIPE_IDS = new Set([
  'pnl_major_driver_decomposition',
  'statement_line_account_movement',
  'opex_category_account_movement',
  'cash_account_movement',
])

// Successive drilldown chips in one analysis session re-read the same render;
// cache briefly per API client so they don't refetch the full statement bundle.
const SERVER_STATEMENTS_CACHE_TTL_MS = 60_000
type ServerStatementsCacheEntry = { at: number; promise: Promise<QuickBooksCfoStatementsRenderContractResponse> }
const serverStatementsCacheByApi = new WeakMap<object, Map<string, ServerStatementsCacheEntry>>()

const fetchServerStatementsRender = (
  appApi: CfoDrilldownServerContext['appApi'],
  workbookId: string,
  asOf: string | undefined,
): Promise<QuickBooksCfoStatementsRenderContractResponse> => {
  let cache = serverStatementsCacheByApi.get(appApi)
  if (!cache) {
    cache = new Map()
    serverStatementsCacheByApi.set(appApi, cache)
  }
  const key = `${workbookId}:${asOf || 'latest'}`
  const cached = cache.get(key)
  if (cached && Date.now() - cached.at < SERVER_STATEMENTS_CACHE_TTL_MS) return cached.promise
  const promise = appApi.getQuickBooksCfoStatementsRender!(workbookId, asOf ? { asOf, grain: 'month' } : { grain: 'month' })
  cache.set(key, { at: Date.now(), promise })
  promise.catch(() => {
    cache!.delete(key)
  })
  return promise
}

const serverStatementsNotReady = (
  recipe: CfoDrilldownRecipeManifest,
  summary: string,
  source: string = 'server_statements_render',
): CfoDrilldownRecipeResult => ({
  status: 'not_ready',
  recipe_id: recipe.id,
  recipe_label: recipe.label,
  manifest_version: CFO_DRILLDOWN_MANIFEST.version,
  summary,
  freshness: { source },
  fallback_policy: { custom_sql_allowed: false, custom_sql_scope: 'manifest_views_only' },
  fallback_tools: [...CFO_DRILLDOWN_FALLBACK_TOOLS],
  next_step_hint: CFO_DRILLDOWN_FALLBACK_HINT,
})

type ServerPnlBucket = {
  id: 'revenue' | 'cost_of_sales' | 'operating_expenses' | 'other_pnl'
  label: string
  incomeLike: boolean
}

const classifyServerPnlRow = (row: QuickBooksFpaStatementRow): ServerPnlBucket | null => {
  const type = trimStr(row.qbo_account_type).toLowerCase()
  const cls = trimStr(row.qbo_classification).toLowerCase()
  if (type.includes('cost of goods') || type.includes('cogs')) return { id: 'cost_of_sales', label: 'COGS', incomeLike: false }
  if (type.includes('other income')) return { id: 'other_pnl', label: 'Other P&L', incomeLike: true }
  if (type.includes('other expense')) return { id: 'other_pnl', label: 'Other P&L', incomeLike: false }
  if (cls === 'revenue' || type.includes('income') || type.includes('revenue')) return { id: 'revenue', label: 'Revenue', incomeLike: true }
  if (cls === 'expense' || type.includes('expense')) return { id: 'operating_expenses', label: 'OpEx', incomeLike: false }
  return null
}

const isServerStatementLeafRow = (row: QuickBooksFpaStatementRow): boolean =>
  Boolean(trimStr(row?.account_id)) && !row.is_total && !row.is_formula

const isServerCashRow = (row: QuickBooksFpaStatementRow): boolean => {
  const type = trimStr(row.qbo_account_type).toLowerCase()
  const label = trimStr(row.label).toLowerCase()
  return type.includes('bank') || label.includes('cash') || label.includes('bank')
}

const serverPeriodIndexes = (periods: string[], start: string, endExclusive: string): number[] => {
  const out: number[] = []
  periods.forEach((period, idx) => {
    const month = normalizeMonthStart(period)
    if (month && month >= start && month < endExclusive) out.push(idx)
  })
  return out
}

const sumServerRowValues = (row: QuickBooksFpaStatementRow, indexes: number[]): number =>
  indexes.reduce((sum, idx) => sum + num(row.values?.[idx]), 0)

/** Balance-sheet semantics: the balance at the last covered month of a window. */
const lastServerRowValue = (row: QuickBooksFpaStatementRow, indexes: number[]): number => {
  for (let i = indexes.length - 1; i >= 0; i--) {
    const value = Number(row.values?.[indexes[i]])
    if (Number.isFinite(value)) return value
  }
  return 0
}

type ServerAccountEntry = {
  account_id: string
  account_label: string
  bucket: ServerPnlBucket
  category_label: string
  current_value: number
  prior_value: number
  delta_abs: number
  net_income_effect: number
}

const serverCategoryLabelFor = (
  row: QuickBooksFpaStatementRow,
  rowsById: Map<string, QuickBooksFpaStatementRow>,
): string => {
  let cursor: QuickBooksFpaStatementRow | undefined = row
  for (let hops = 0; hops < 8 && cursor; hops++) {
    const parent: QuickBooksFpaStatementRow | undefined = cursor.parent_node_id
      ? rowsById.get(cursor.parent_node_id)
      : undefined
    if (!parent) break
    const label = trimStr(parent.label)
    if (label && parent.level >= 1) return label
    cursor = parent
  }
  return ''
}

const collectServerPnlAccounts = (
  render: QuickBooksFpaStatementRender,
  window: CfoDrilldownWindow,
): { accounts: ServerAccountEntry[]; windowCovered: boolean } => {
  const periods = Array.isArray(render.periods) ? render.periods : []
  const currentIdxs = serverPeriodIndexes(periods, window.currentStart, window.currentEndExclusive)
  const priorIdxs = serverPeriodIndexes(periods, window.priorStart, window.priorEndExclusive)
  if (!currentIdxs.length || !priorIdxs.length) return { accounts: [], windowCovered: false }
  const rowsById = new Map((render.rows || []).map((row) => [row.node_id, row]))
  const accounts: ServerAccountEntry[] = []
  for (const row of render.rows || []) {
    if (!isServerStatementLeafRow(row)) continue
    const bucket = classifyServerPnlRow(row)
    if (!bucket) continue
    const currentValue = sumServerRowValues(row, currentIdxs)
    const priorValue = sumServerRowValues(row, priorIdxs)
    const delta = currentValue - priorValue
    if (Math.abs(delta) <= 0.005 && Math.abs(currentValue) <= 0.005 && Math.abs(priorValue) <= 0.005) continue
    accounts.push({
      account_id: trimStr(row.account_id),
      account_label: trimStr(row.label) || trimStr(row.account_id),
      bucket,
      category_label: serverCategoryLabelFor(row, rowsById) || bucket.label,
      current_value: currentValue,
      prior_value: priorValue,
      delta_abs: delta,
      net_income_effect: bucket.incomeLike
        ? incomeNetIncomeEffect(delta, currentValue, priorValue)
        : expenseNetIncomeEffect(delta),
    })
  }
  return { accounts, windowCovered: true }
}

const collectServerCashAccounts = (
  render: QuickBooksFpaStatementRender,
  window: CfoDrilldownWindow,
): { accounts: ServerAccountEntry[]; windowCovered: boolean } => {
  const periods = Array.isArray(render.periods) ? render.periods : []
  const currentIdxs = serverPeriodIndexes(periods, window.currentStart, window.currentEndExclusive)
  const priorIdxs = serverPeriodIndexes(periods, window.priorStart, window.priorEndExclusive)
  if (!currentIdxs.length || !priorIdxs.length) return { accounts: [], windowCovered: false }
  const rowsById = new Map((render.rows || []).map((row) => [row.node_id, row]))
  const bucket: ServerPnlBucket = { id: 'other_pnl', label: 'Cash', incomeLike: false }
  const accounts: ServerAccountEntry[] = []
  for (const row of render.rows || []) {
    if (!isServerStatementLeafRow(row) || !isServerCashRow(row)) continue
    const currentValue = lastServerRowValue(row, currentIdxs)
    const priorValue = lastServerRowValue(row, priorIdxs)
    const delta = currentValue - priorValue
    if (Math.abs(delta) <= 0.005 && Math.abs(currentValue) <= 0.005 && Math.abs(priorValue) <= 0.005) continue
    accounts.push({
      account_id: trimStr(row.account_id),
      account_label: trimStr(row.label) || trimStr(row.account_id),
      bucket,
      category_label: serverCategoryLabelFor(row, rowsById) || 'Cash and equivalents',
      current_value: currentValue,
      prior_value: priorValue,
      delta_abs: delta,
      net_income_effect: delta,
    })
  }
  return { accounts, windowCovered: true }
}

const resolveServerDrilldownWindow = (
  args: Record<string, any>,
  asOfHint: string | null,
  response: QuickBooksCfoStatementsRenderContractResponse,
  render: QuickBooksFpaStatementRender | null | undefined,
): CfoDrilldownWindow | null => {
  const comparisonKind = requestedCfoDrilldownComparisonKind(args, 'mom')
  const anchor =
    normalizeMonthStart(args.as_of ?? args.asOf ?? asOfHint) ||
    normalizeMonthStart(response.as_of) ||
    normalizeMonthStart(render?.periods?.[render.periods.length - 1])
  if (!anchor) return null
  return buildCfoDrilldownWindow(anchor, comparisonKind)
}

const buildStatementLineRecipe = async (
  workerClient: AppWorkerClient,
  duckDbOpts: Record<string, any> | null | undefined,
  recipe: CfoDrilldownRecipeManifest,
  args: Record<string, any>,
  asOfHint: string | null,
  artifactReadiness: CfoDrilldownArtifactReadiness[],
): Promise<CfoDrilldownRecipeResult> => {
  const metricId = normalizeCfoDrilldownMetricId(args.metric_id ?? args.metricId ?? args.metric ?? args.selected_metric_id)
  const comparisonKind = requestedCfoDrilldownComparisonKind(args, 'mom')
  const asOf = normalizeMonthStart(args.as_of ?? args.asOf ?? asOfHint)
  const window = buildCfoDrilldownWindow(asOf, comparisonKind)
  if (!window) return recipeNotReady(recipe, 'CFO drilldown could not determine a selected comparison window from local artifacts.', [], artifactReadiness)
  const metricLabel = trimStr(args.metric_label ?? args.metricLabel) || metricLabelFor(metricId, metricId)
  const viewTable = recipe.id === 'opex_category_account_movement'
    ? CFO_DRILLDOWN_VIEWS_BY_ID.get('opex_monthly_activity')?.table || 'fg_cfo_ai_opex_monthly_activity'
    : CFO_DRILLDOWN_VIEWS_BY_ID.get('statement_monthly_activity')?.table || 'fg_cfo_ai_statement_monthly_activity'
  const filterSql = recipe.id === 'opex_category_account_movement' ? 'true' : statementMetricFilterSql(metricId)
  const rows = await runDuckRows(
    workerClient,
    `
WITH scoped AS (
  SELECT
    account_id,
    account_label,
    category_label,
    CAST(date_trunc('month', TRY_CAST(period_start AS DATE)) AS VARCHAR) AS period_start,
    coalesce(TRY_CAST(monthly_activity AS DOUBLE), TRY_CAST(amount AS DOUBLE), 0) AS amount,
    source_table_names
  FROM ${quoteDuckIdent(viewTable)}
  WHERE TRY_CAST(period_start AS DATE) >= TRY_CAST(${quoteDuckString(window.priorStart)} AS DATE)
    AND TRY_CAST(period_start AS DATE) < TRY_CAST(${quoteDuckString(window.currentEndExclusive)} AS DATE)
    AND (${filterSql})
)
SELECT
  account_id,
  account_label,
  category_label,
  SUM(CASE WHEN TRY_CAST(period_start AS DATE) >= TRY_CAST(${quoteDuckString(window.currentStart)} AS DATE)
      AND TRY_CAST(period_start AS DATE) < TRY_CAST(${quoteDuckString(window.currentEndExclusive)} AS DATE) THEN amount ELSE 0 END) AS current_value,
  SUM(CASE WHEN TRY_CAST(period_start AS DATE) >= TRY_CAST(${quoteDuckString(window.priorStart)} AS DATE)
      AND TRY_CAST(period_start AS DATE) < TRY_CAST(${quoteDuckString(window.priorEndExclusive)} AS DATE) THEN amount ELSE 0 END) AS prior_value,
  SUM(CASE WHEN TRY_CAST(period_start AS DATE) >= TRY_CAST(${quoteDuckString(window.currentStart)} AS DATE)
      AND TRY_CAST(period_start AS DATE) < TRY_CAST(${quoteDuckString(window.currentEndExclusive)} AS DATE) THEN amount ELSE 0 END)
    - SUM(CASE WHEN TRY_CAST(period_start AS DATE) >= TRY_CAST(${quoteDuckString(window.priorStart)} AS DATE)
      AND TRY_CAST(period_start AS DATE) < TRY_CAST(${quoteDuckString(window.priorEndExclusive)} AS DATE) THEN amount ELSE 0 END) AS delta_abs,
  string_agg(DISTINCT source_table_names, ',') AS source_table_names
FROM scoped
GROUP BY 1, 2, 3
HAVING ABS(delta_abs) > 0.005 OR ABS(current_value) > 0.005 OR ABS(prior_value) > 0.005
ORDER BY ABS(delta_abs) DESC
LIMIT 500`,
    { previewRowLimit: 600, timeoutMs: 90000, caller: `CfoDrilldownRecipes:run_drilldown_recipe:${recipe.id}` },
    duckDbOpts,
  )
  const expenseLike = recipe.id === 'opex_category_account_movement' || isExpenseLikeMetricId(metricId)
  const pAndLMetric = ['revenue', 'cost_of_sales', 'operating_expenses'].includes(metricId)
  const accountRows = rows.map((row) => {
    const delta = num(row.delta_abs)
    const currentValue = num(row.current_value)
    const priorValue = num(row.prior_value)
    const effect = metricId === 'revenue'
      ? incomeNetIncomeEffect(delta, currentValue, priorValue)
      : pAndLMetric || expenseLike
        ? expenseNetIncomeEffect(delta)
        : delta
    return {
      account_id: trimStr(row.account_id),
      account_label: trimStr(row.account_label) || trimStr(row.account_id),
      category_id: trimStr(row.category_label) || metricLabel,
      category_label: trimStr(row.category_label) || metricLabel,
      current_value: currentValue,
      prior_value: priorValue,
      delta_abs: delta,
      net_income_effect: effect,
      profit_effect: effect,
      source_table_names: trimStr(row.source_table_names).split(',').filter(Boolean),
    }
  })
  return assembleStatementLineRecipeResult({
    recipe,
    args,
    window,
    comparisonKind,
    metricId,
    metricLabel,
    accountRows,
    expenseLike,
    freshness: { source: 'local_duckdb', tables: [viewTable], artifact_readiness: artifactReadiness },
    fallbackUsed: false,
  })
}

type StatementLineAccountRow = {
  account_id: string
  account_label: string
  category_id: string
  category_label: string
  current_value: number
  prior_value: number
  delta_abs: number
  net_income_effect: number
  profit_effect: number
  source_table_names: string[]
}

const assembleStatementLineRecipeResult = (opts: {
  recipe: CfoDrilldownRecipeManifest
  args: Record<string, any>
  window: CfoDrilldownWindow
  comparisonKind: MetricComparisonKind
  metricId: string
  metricLabel: string
  accountRows: StatementLineAccountRow[]
  expenseLike: boolean
  freshness: NonNullable<CfoDrilldownRecipeResult['freshness']>
  fallbackUsed: boolean
}): CfoDrilldownRecipeResult => {
  const { recipe, args, window, comparisonKind, metricId, metricLabel, accountRows, expenseLike, freshness, fallbackUsed } = opts
  const categoryMap = new Map<string, Record<string, any>>()
  for (const row of accountRows) {
    const key = row.category_id
    const existing = categoryMap.get(key) || {
      category_id: key,
      category_label: row.category_label,
      current_value: 0,
      prior_value: 0,
      delta_abs: 0,
      net_income_effect: 0,
      profit_effect: 0,
      account_count: 0,
      account_ids: [],
      source_table_names: new Set<string>(),
    }
    existing.current_value += row.current_value
    existing.prior_value += row.prior_value
    existing.delta_abs += row.delta_abs
    existing.net_income_effect += row.net_income_effect
    existing.profit_effect += row.profit_effect
    existing.account_count += 1
    existing.account_ids.push(row.account_id)
    row.source_table_names.forEach((name: string) => existing.source_table_names.add(name))
    categoryMap.set(key, existing)
  }
  const categoryRows = Array.from(categoryMap.values())
    .map((row): Record<string, any> => ({ ...row, source_table_names: Array.from(row.source_table_names) }))
    .sort((a, b) => Math.abs(num(b.delta_abs)) - Math.abs(num(a.delta_abs)))
  const totalDelta = categoryRows.reduce((sum, row) => sum + num(row.delta_abs), 0)
  const totalEffect = categoryRows.reduce((sum, row) => sum + num(row.net_income_effect), 0)
  const firstCategory = categoryRows[0]?.category_id
  const accountChartRows = accountRows
    .filter((row) => !firstCategory || row.category_id === firstCategory)
    .sort((a, b) => Math.abs(num(b.delta_abs)) - Math.abs(num(a.delta_abs)))
    .slice(0, 8)
  const selectedEffect = Number.isFinite(Number(args.selected_net_income_effect))
    ? Number(args.selected_net_income_effect)
    : Number.isFinite(Number(args.selectedNetIncomeEffect))
      ? Number(args.selectedNetIncomeEffect)
      : null
  const selectedDelta = Number.isFinite(Number(args.selected_line_delta))
    ? Number(args.selected_line_delta)
    : Number.isFinite(Number(args.selectedLineDelta))
      ? Number(args.selectedLineDelta)
      : args.selection && typeof args.selection === 'object' && Number.isFinite(Number(args.selection.metric_delta))
        ? Number(args.selection.metric_delta)
        : Number.isFinite(Number(args.selected_waterfall_delta))
          ? Number(args.selected_waterfall_delta)
          : Number.isFinite(Number(args.selectedWaterfallDelta))
            ? Number(args.selectedWaterfallDelta)
            : null
  const headlineEffect = selectedEffect ?? totalEffect
  const lineDelta = selectedDelta ?? totalDelta
  const lineDeltaText = Math.abs(lineDelta - headlineEffect) > 0.005
    ? `; line movement ${lineDelta >= 0 ? '+' : '-'}${Math.round(Math.abs(lineDelta))}`
    : ''
  const viewKind = categoryRows.length > (CFO_DRILLDOWN_MANIFEST.default_render_policy?.dense_row_threshold ?? 12) ? 'table' : 'bar'
  const charts: CfoDrilldownChartSpec[] = [
    {
      kind: viewKind,
      title: `${metricLabel} movement by category`,
      subtitle: `${window.shortLabel}: selected comparison window only.`,
      items: categoryRows.slice(0, 8).map((row) => ({
        label: row.category_label,
        value: row.delta_abs,
        direction: expenseLike
          ? row.delta_abs > 0 ? 'down' : row.delta_abs < 0 ? 'up' : 'neutral'
          : row.delta_abs > 0 ? 'up' : row.delta_abs < 0 ? 'down' : 'neutral',
        note: expenseLike
          ? row.delta_abs >= 0 ? 'higher expense' : 'lower expense'
          : row.delta_abs >= 0 ? 'higher value' : 'lower value',
        prompt: `Which accounts drove ${row.category_label} ${metricLabel} movement?`,
      })),
      rows: categoryRows.slice(0, 20),
      columns: [
        { key: 'category_label', label: 'Category' },
        { key: 'current_value', label: 'Current', align: 'right' },
        { key: 'prior_value', label: 'Prior', align: 'right' },
        { key: 'delta_abs', label: 'Change', align: 'right' },
        { key: 'net_income_effect', label: 'NI effect', align: 'right' },
      ],
    },
  ]
  if (accountChartRows.length) {
    charts.push({
      kind: 'bar',
      title: firstCategory
        ? `Accounts inside ${categoryRows.find((row) => row.category_id === firstCategory)?.category_label || firstCategory}`
        : `${metricLabel} account movement`,
      subtitle: `${window.shortLabel}: account-level selected comparison window only.`,
      items: accountChartRows.map((row) => ({
        label: row.account_label,
        value: row.delta_abs,
        direction: expenseLike
          ? row.delta_abs > 0 ? 'down' : row.delta_abs < 0 ? 'up' : 'neutral'
          : row.delta_abs > 0 ? 'up' : row.delta_abs < 0 ? 'down' : 'neutral',
        note: trimStr(row.category_label),
        prompt: `Show transaction-level GL detail for ${row.account_label}.`,
      })),
    })
  }
  return {
    status: 'ready',
    recipe_id: recipe.id,
    recipe_label: recipe.label,
    manifest_version: CFO_DRILLDOWN_MANIFEST.version,
    view_kind: viewKind,
    metric_id: metricId,
    metric_label: metricLabel,
    comparison: comparisonKind,
    comparison_label: window.label,
    as_of: window.currentStart,
    current_window: { start: window.currentStart, end_exclusive: window.currentEndExclusive },
    prior_window: { start: window.priorStart, end_exclusive: window.priorEndExclusive },
    delta_abs: lineDelta,
    net_income_effect: headlineEffect,
    summary: `${metricLabel} bridge effect ${headlineEffect >= 0 ? '+' : '-'}${Math.round(Math.abs(headlineEffect))} ${window.shortLabel}${lineDeltaText}.`,
    category_rows: categoryRows,
    account_rows: accountRows,
    standout_categories: categoryRows.slice(0, 3),
    standout_accounts: accountRows.slice(0, 8),
    chart_specs: charts,
    actions: [
      { label: `Inspect ${metricLabel} accounts`, prompt: `Which accounts drove ${metricLabel} ${window.shortLabel} movement?` },
      { label: 'Check transactions', prompt: `Show transaction-level GL detail for the largest ${metricLabel} account movement.` },
    ],
    freshness,
    fallback_policy: { custom_sql_allowed: Boolean(recipe.supports_custom_sql_fallback), custom_sql_scope: 'manifest_views_only' },
    telemetry: {
      selected_metric_id: metricId,
      source_component: trimStr(args.source_component ?? args.sourceComponent),
      selected_recipe_id: recipe.id,
      readiness_status: 'ready',
      row_count: accountRows.length,
      render_kind: viewKind,
      fallback_used: fallbackUsed,
    },
  }
}

// Server-side statement-line / opex / cash recipe: same result contract as the
// local builder, but computed from the governed statements-render bundle.
const buildStatementLineServerRecipe = async (
  appApi: CfoDrilldownServerContext['appApi'],
  workbookId: string,
  recipe: CfoDrilldownRecipeManifest,
  args: Record<string, any>,
  asOfHint: string | null,
): Promise<CfoDrilldownRecipeResult> => {
  const isCashRecipe = recipe.id === 'cash_account_movement'
  const metricId = recipe.id === 'opex_category_account_movement'
    ? 'operating_expenses'
    : isCashRecipe
      ? normalizeCfoDrilldownMetricId(args.metric_id ?? args.metricId) || 'cash'
      : normalizeCfoDrilldownMetricId(args.metric_id ?? args.metricId ?? args.metric ?? args.selected_metric_id)
  const metricLabel = trimStr(args.metric_label ?? args.metricLabel) || metricLabelFor(metricId, metricId)
  let response: QuickBooksCfoStatementsRenderContractResponse
  try {
    response = await fetchServerStatementsRender(appApi, workbookId, normalizeMonthStart(args.as_of ?? args.asOf ?? asOfHint) || undefined)
  } catch (err) {
    return serverStatementsNotReady(recipe, `Governed statements render could not be loaded: ${(err as Error)?.message || 'request failed'}.`)
  }
  const render = isCashRecipe ? response?.balance_sheet : response?.profit_and_loss
  if (!render || !Array.isArray(render.rows) || !render.rows.length) {
    return serverStatementsNotReady(recipe, `The governed statements render has no ${isCashRecipe ? 'balance sheet' : 'P&L'} rows for this workbook yet.`)
  }
  const window = resolveServerDrilldownWindow(args, asOfHint, response, render)
  if (!window) return serverStatementsNotReady(recipe, 'CFO drilldown could not determine a comparison window from the statements render.')
  const comparisonKind = requestedCfoDrilldownComparisonKind(args, 'mom')
  const collected = isCashRecipe
    ? collectServerCashAccounts(render, window)
    : collectServerPnlAccounts(render, window)
  if (!collected.windowCovered) {
    return serverStatementsNotReady(recipe, `The governed statements render does not cover the ${window.label} window ending ${window.currentStart}.`)
  }
  const scoped = isCashRecipe
    ? collected.accounts
    : ['revenue', 'cost_of_sales', 'operating_expenses'].includes(metricId)
      ? collected.accounts.filter((account) => account.bucket.id === metricId)
      : collected.accounts
  const accountRows: StatementLineAccountRow[] = scoped
    .map((account) => ({
      account_id: account.account_id,
      account_label: account.account_label,
      category_id: account.category_label || metricLabel,
      category_label: account.category_label || metricLabel,
      current_value: account.current_value,
      prior_value: account.prior_value,
      delta_abs: account.delta_abs,
      net_income_effect: account.net_income_effect,
      profit_effect: account.net_income_effect,
      source_table_names: ['qbo_statements_render'],
    }))
    .sort((a, b) => Math.abs(b.delta_abs) - Math.abs(a.delta_abs))
    .slice(0, 500)
  return assembleStatementLineRecipeResult({
    recipe,
    args,
    window,
    comparisonKind,
    metricId,
    metricLabel,
    accountRows,
    expenseLike: recipe.id === 'opex_category_account_movement' || isExpenseLikeMetricId(metricId),
    freshness: { source: 'server_statements_render' },
    fallbackUsed: true,
  })
}

// When the net-income bridge already computed driver rows for the exact bar
// the user clicked, those override the recomputed decomposition (both the
// local-DuckDB and server builders honor this).
const selectionDriverRows = (args: Record<string, any>): Array<Record<string, any>> => {
  const selection = args.selection && typeof args.selection === 'object' ? args.selection as Record<string, any> : {}
  return Array.isArray(selection.drivers)
    ? selection.drivers
        .filter((row): row is Record<string, any> => row != null && typeof row === 'object')
        .map((row) => {
          const label = trimStr(row.label)
          const delta = num(row.metric_delta_abs)
          const effect = num(row.net_income_effect)
          return {
            label,
            driver_type: normalizeCfoDrilldownMetricId(row.id) === 'revenue' ? 'income' : 'expense',
            current_value: num(row.current_value),
            prior_value: num(row.prior_value),
            delta_abs: delta,
            net_income_effect: effect,
            profit_effect: effect,
            source_table_names: ['net_income_bridge'],
          }
        })
        .filter((row) => row.label && Number.isFinite(row.net_income_effect))
    : []
}

// Gross-profit-altitude metrics: their decomposition is Revenue vs COGS only —
// OpEx movement cannot move gross profit/margin, so showing it as a "driver"
// would be factually wrong.
const GROSS_METRIC_IDS = new Set(['gross_profit', 'gross_margin', 'gross_margin_pct', 'margin'])

const PNL_DRIVER_CHART_PROMPT = (label: string): string =>
  label === 'Revenue'
    ? 'Why did Revenue change?'
    : label === 'COGS'
      ? 'Why did COGS change?'
      : label === 'OpEx'
        ? 'Why did OpEx change?'
        : `Why did ${label} change?`

// Server-side major P&L driver decomposition: Revenue / COGS / OpEx / Other
// movement plus the largest account-level movers, computed from the governed
// statements-render contract. Used when local DuckDB (or its warmed semantic
// views) is unavailable — e.g. the lightweight CFO Home page.
const buildPnlDriverServerRecipe = async (
  appApi: CfoDrilldownServerContext['appApi'],
  workbookId: string,
  recipe: CfoDrilldownRecipeManifest,
  args: Record<string, any>,
  asOfHint: string | null,
): Promise<CfoDrilldownRecipeResult> => {
  let response: QuickBooksCfoStatementsRenderContractResponse
  try {
    response = await fetchServerStatementsRender(appApi, workbookId, normalizeMonthStart(args.as_of ?? args.asOf ?? asOfHint) || undefined)
  } catch (err) {
    return serverStatementsNotReady(recipe, `Governed statements render could not be loaded: ${(err as Error)?.message || 'request failed'}.`)
  }
  const pnl = response?.profit_and_loss
  if (!pnl || !Array.isArray(pnl.rows) || !pnl.rows.length) {
    return serverStatementsNotReady(recipe, 'The governed statements render has no P&L rows for this workbook yet.')
  }
  const window = resolveServerDrilldownWindow(args, asOfHint, response, pnl)
  if (!window) return serverStatementsNotReady(recipe, 'CFO drilldown could not determine a comparison window from the statements render.')
  const comparisonKind = requestedCfoDrilldownComparisonKind(args, 'mom')
  const metricId = normalizeCfoDrilldownMetricId(args.metric_id ?? args.metricId ?? 'net_income') || 'net_income'
  const grossScope = GROSS_METRIC_IDS.has(metricId)
  const collected = collectServerPnlAccounts(pnl, window)
  if (!collected.windowCovered) {
    return serverStatementsNotReady(recipe, `The governed statements render does not cover the ${window.label} window ending ${window.currentStart}.`)
  }
  const accounts = grossScope
    ? collected.accounts.filter((account) => account.bucket.id === 'revenue' || account.bucket.id === 'cost_of_sales')
    : collected.accounts
  const bucketMap = new Map<string, Record<string, any>>()
  for (const account of accounts) {
    const existing = bucketMap.get(account.bucket.label) || {
      label: account.bucket.label,
      driver_type: account.bucket.incomeLike ? 'income' : 'expense',
      current_value: 0,
      prior_value: 0,
      delta_abs: 0,
      net_income_effect: 0,
      profit_effect: 0,
      source_table_names: ['qbo_statements_render'],
    }
    existing.current_value += account.current_value
    existing.prior_value += account.prior_value
    existing.delta_abs += account.delta_abs
    existing.net_income_effect += account.net_income_effect
    existing.profit_effect += account.net_income_effect
    bucketMap.set(account.bucket.label, existing)
  }
  const selectionDrivers = selectionDriverRows(args)
  const drivers = (selectionDrivers.length ? selectionDrivers : Array.from(bucketMap.values()))
    .sort((a, b) => Math.abs(num(b.net_income_effect)) - Math.abs(num(a.net_income_effect)))
  if (!drivers.length) {
    return serverStatementsNotReady(recipe, `The governed statements render shows no P&L movement for the ${window.label} window ending ${window.currentStart}.`)
  }
  const totalEffect = drivers.reduce((sum, row) => sum + num(row.net_income_effect), 0)
  const accountMovers = accounts
    .slice()
    .sort((a, b) => Math.abs(b.net_income_effect) - Math.abs(a.net_income_effect))
    .slice(0, 20)
  const topMover = accountMovers[0] || null
  const chartSpecs: CfoDrilldownChartSpec[] = [
    {
      kind: 'bar',
      title: 'Major P&L drivers',
      subtitle: `${window.shortLabel}: bars show net-income effect for the selected comparison only.`,
      items: drivers.slice(0, 8).map((row) => ({
        label: trimStr(row.label),
        value: num(row.net_income_effect),
        direction: num(row.net_income_effect) > 0 ? 'up' : num(row.net_income_effect) < 0 ? 'down' : 'neutral',
        note: `${num(row.delta_abs) >= 0 ? '+' : '-'}${Math.round(Math.abs(num(row.delta_abs)))} line movement`,
        prompt: PNL_DRIVER_CHART_PROMPT(trimStr(row.label)),
      })),
      rows: drivers,
      columns: [
        { key: 'label', label: 'Driver' },
        { key: 'current_value', label: 'Current', align: 'right' },
        { key: 'prior_value', label: 'Prior', align: 'right' },
        { key: 'delta_abs', label: 'Line change', align: 'right' },
        { key: 'net_income_effect', label: 'NI effect', align: 'right' },
      ],
    },
  ]
  if (accountMovers.length) {
    chartSpecs.push({
      kind: 'bar',
      title: 'Largest account movers',
      subtitle: `${window.shortLabel}: account-level net-income effect from the governed statements.`,
      items: accountMovers.slice(0, 8).map((account) => ({
        label: account.account_label,
        value: account.net_income_effect,
        direction: account.net_income_effect > 0 ? 'up' : account.net_income_effect < 0 ? 'down' : 'neutral',
        note: account.category_label,
        prompt: `Why did ${account.account_label} change ${window.shortLabel}?`,
      })),
      rows: accountMovers,
      columns: [
        { key: 'account_label', label: 'Account' },
        { key: 'category_label', label: 'Category' },
        { key: 'current_value', label: 'Current', align: 'right' },
        { key: 'prior_value', label: 'Prior', align: 'right' },
        { key: 'net_income_effect', label: 'NI effect', align: 'right' },
      ],
    })
  }
  return {
    status: 'ready',
    recipe_id: recipe.id,
    recipe_label: recipe.label,
    manifest_version: CFO_DRILLDOWN_MANIFEST.version,
    view_kind: 'bar',
    metric_id: metricId,
    metric_label: trimStr(args.metric_label ?? args.metricLabel) || 'Net Income',
    comparison: comparisonKind,
    comparison_label: window.label,
    as_of: window.currentStart,
    current_window: { start: window.currentStart, end_exclusive: window.currentEndExclusive },
    prior_window: { start: window.priorStart, end_exclusive: window.priorEndExclusive },
    net_income_effect: totalEffect,
    summary: `${trimStr(args.metric_label ?? args.metricLabel) || 'Net income'} driver recipe found ${drivers.length} major P&L driver(s) for ${window.shortLabel}${grossScope ? ' (Revenue and COGS only — OpEx cannot move a gross metric)' : ''}; total directional effect ${totalEffect >= 0 ? '+' : '-'}${Math.round(Math.abs(totalEffect))}${topMover ? `; largest account mover ${topMover.account_label} (${topMover.net_income_effect >= 0 ? '+' : '-'}${Math.round(Math.abs(topMover.net_income_effect))} NI effect)` : ''}.`,
    driver_rows: drivers,
    account_rows: accountMovers,
    standout_accounts: accountMovers.slice(0, 8),
    chart_specs: chartSpecs,
    actions: [
      { label: 'Inspect Revenue', prompt: 'Why did Revenue change?' },
      { label: 'Inspect COGS', prompt: 'Why did COGS change?' },
      ...(grossScope ? [] : [{ label: 'Inspect OpEx', prompt: 'Why did OpEx change?' }]),
    ],
    freshness: { source: 'server_statements_render' },
    fallback_policy: { custom_sql_allowed: false, custom_sql_scope: 'manifest_views_only' },
    telemetry: {
      selected_metric_id: metricId,
      source_component: trimStr(args.source_component ?? args.sourceComponent),
      selected_recipe_id: recipe.id,
      readiness_status: 'ready',
      row_count: drivers.length,
      render_kind: 'bar',
      fallback_used: true,
    },
  }
}

const buildPnlDriverRecipe = async (
  workerClient: AppWorkerClient,
  duckDbOpts: Record<string, any> | null | undefined,
  recipe: CfoDrilldownRecipeManifest,
  args: Record<string, any>,
  asOfHint: string | null,
  artifactReadiness: CfoDrilldownArtifactReadiness[],
): Promise<CfoDrilldownRecipeResult> => {
  const comparisonKind = requestedCfoDrilldownComparisonKind(args, 'mom')
  const window = buildCfoDrilldownWindow(normalizeMonthStart(args.as_of ?? args.asOf ?? asOfHint), comparisonKind)
  if (!window) return recipeNotReady(recipe, 'CFO drilldown could not determine a selected comparison window from local artifacts.', [], artifactReadiness)
  const table = CFO_DRILLDOWN_VIEWS_BY_ID.get('statement_monthly_activity')?.table || 'fg_cfo_ai_statement_monthly_activity'
  const rows = await runDuckRows(
    workerClient,
    `
WITH scoped AS (
  SELECT
    CASE
      WHEN ${statementMetricFilterSql('revenue')} THEN 'Revenue'
      WHEN ${statementMetricFilterSql('cost_of_sales')} THEN 'COGS'
      WHEN ${statementMetricFilterSql('operating_expenses')} THEN 'OpEx'
      ELSE 'Other P&L'
    END AS label,
    CASE
      WHEN ${statementMetricFilterSql('revenue')} THEN 'income'
      WHEN ${statementMetricFilterSql('cost_of_sales')} THEN 'expense'
      WHEN ${statementMetricFilterSql('operating_expenses')} THEN 'expense'
      ELSE 'other'
    END AS driver_type,
    CAST(date_trunc('month', TRY_CAST(period_start AS DATE)) AS VARCHAR) AS period_start,
    coalesce(TRY_CAST(monthly_activity AS DOUBLE), TRY_CAST(amount AS DOUBLE), 0) AS amount,
    source_table_names
  FROM ${quoteDuckIdent(table)}
  WHERE statement_type = 'profit_and_loss'
    AND TRY_CAST(period_start AS DATE) >= TRY_CAST(${quoteDuckString(window.priorStart)} AS DATE)
    AND TRY_CAST(period_start AS DATE) < TRY_CAST(${quoteDuckString(window.currentEndExclusive)} AS DATE)
)
SELECT
  label,
  driver_type,
  SUM(CASE WHEN TRY_CAST(period_start AS DATE) >= TRY_CAST(${quoteDuckString(window.currentStart)} AS DATE)
      AND TRY_CAST(period_start AS DATE) < TRY_CAST(${quoteDuckString(window.currentEndExclusive)} AS DATE) THEN amount ELSE 0 END) AS current_value,
  SUM(CASE WHEN TRY_CAST(period_start AS DATE) >= TRY_CAST(${quoteDuckString(window.priorStart)} AS DATE)
      AND TRY_CAST(period_start AS DATE) < TRY_CAST(${quoteDuckString(window.priorEndExclusive)} AS DATE) THEN amount ELSE 0 END) AS prior_value,
  SUM(CASE WHEN TRY_CAST(period_start AS DATE) >= TRY_CAST(${quoteDuckString(window.currentStart)} AS DATE)
      AND TRY_CAST(period_start AS DATE) < TRY_CAST(${quoteDuckString(window.currentEndExclusive)} AS DATE) THEN amount ELSE 0 END)
    - SUM(CASE WHEN TRY_CAST(period_start AS DATE) >= TRY_CAST(${quoteDuckString(window.priorStart)} AS DATE)
      AND TRY_CAST(period_start AS DATE) < TRY_CAST(${quoteDuckString(window.priorEndExclusive)} AS DATE) THEN amount ELSE 0 END) AS delta_abs,
  string_agg(DISTINCT source_table_names, ',') AS source_table_names
FROM scoped
GROUP BY 1, 2
HAVING ABS(delta_abs) > 0.005 OR ABS(current_value) > 0.005 OR ABS(prior_value) > 0.005
ORDER BY ABS(delta_abs) DESC`,
    { previewRowLimit: 50, timeoutMs: 90000, caller: `CfoDrilldownRecipes:run_drilldown_recipe:${recipe.id}` },
    duckDbOpts,
  )
  const metricId = normalizeCfoDrilldownMetricId(args.metric_id ?? args.metricId ?? 'net_income') || 'net_income'
  const grossScope = GROSS_METRIC_IDS.has(metricId)
  const scopedRows = grossScope
    ? rows.filter((row) => ['Revenue', 'COGS'].includes(trimStr(row.label)))
    : rows
  const selectionDrivers = selectionDriverRows(args)
  const drivers = (selectionDrivers.length ? selectionDrivers : scopedRows.map((row) => {
    const delta = num(row.delta_abs)
    const driverType = trimStr(row.driver_type)
    const currentValue = num(row.current_value)
    const priorValue = num(row.prior_value)
    const effect = driverType === 'income'
      ? incomeNetIncomeEffect(delta, currentValue, priorValue)
      : driverType === 'expense'
        ? expenseNetIncomeEffect(delta)
        : delta
    return {
      label: trimStr(row.label),
      driver_type: driverType,
      current_value: currentValue,
      prior_value: priorValue,
      delta_abs: delta,
      net_income_effect: effect,
      profit_effect: effect,
      source_table_names: trimStr(row.source_table_names).split(',').filter(Boolean),
    }
  })).sort((a, b) => Math.abs(num(b.net_income_effect)) - Math.abs(num(a.net_income_effect)))
  const totalEffect = drivers.reduce((sum, row) => sum + num(row.net_income_effect), 0)
  return {
    status: 'ready',
    recipe_id: recipe.id,
    recipe_label: recipe.label,
    manifest_version: CFO_DRILLDOWN_MANIFEST.version,
    view_kind: 'bar',
    metric_id: metricId,
    metric_label: trimStr(args.metric_label ?? args.metricLabel) || 'Net Income',
    comparison: comparisonKind,
    comparison_label: window.label,
    as_of: window.currentStart,
    current_window: { start: window.currentStart, end_exclusive: window.currentEndExclusive },
    prior_window: { start: window.priorStart, end_exclusive: window.priorEndExclusive },
    net_income_effect: totalEffect,
    summary: `${trimStr(args.metric_label ?? args.metricLabel) || 'Net income'} driver recipe found ${drivers.length} major P&L driver(s) for ${window.shortLabel}${grossScope ? ' (Revenue and COGS only — OpEx cannot move a gross metric)' : ''}; total directional effect ${totalEffect >= 0 ? '+' : '-'}${Math.round(Math.abs(totalEffect))}.`,
    driver_rows: drivers,
    chart_specs: [
      {
        kind: 'bar',
        title: 'Major P&L drivers',
        subtitle: `${window.shortLabel}: bars show net-income effect for the selected comparison only.`,
        items: drivers.slice(0, 8).map((row) => ({
          label: row.label,
          value: row.net_income_effect,
          direction: row.net_income_effect > 0 ? 'up' : row.net_income_effect < 0 ? 'down' : 'neutral',
          note: `${row.delta_abs >= 0 ? '+' : '-'}${Math.round(Math.abs(row.delta_abs))} line movement`,
          prompt: row.label === 'Revenue'
            ? 'Why did Revenue change?'
            : row.label === 'COGS'
              ? 'Why did COGS change?'
              : row.label === 'OpEx'
                ? 'Why did OpEx change?'
                : `Why did ${row.label} change?`,
        })),
        rows: drivers,
        columns: [
          { key: 'label', label: 'Driver' },
          { key: 'current_value', label: 'Current', align: 'right' },
          { key: 'prior_value', label: 'Prior', align: 'right' },
          { key: 'delta_abs', label: 'Line change', align: 'right' },
          { key: 'net_income_effect', label: 'NI effect', align: 'right' },
        ],
      },
    ],
    actions: [
      { label: 'Inspect Revenue', prompt: 'Why did Revenue change?' },
      { label: 'Inspect COGS', prompt: 'Why did COGS change?' },
      ...(grossScope ? [] : [{ label: 'Inspect OpEx', prompt: 'Why did OpEx change?' }]),
    ],
    freshness: { source: 'local_duckdb', tables: [table], artifact_readiness: artifactReadiness },
    fallback_policy: { custom_sql_allowed: Boolean(recipe.supports_custom_sql_fallback), custom_sql_scope: 'manifest_views_only' },
    telemetry: {
      selected_metric_id: metricId,
      source_component: trimStr(args.source_component ?? args.sourceComponent),
      selected_recipe_id: recipe.id,
      readiness_status: 'ready',
      row_count: drivers.length,
      render_kind: 'bar',
      fallback_used: false,
    },
  }
}

const parseJsonPayload = (value: unknown): Record<string, any> => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, any>
  try {
    const parsed = JSON.parse(trimStr(value))
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

const payloadNumber = (row: Record<string, any>, keys: string[]): number => {
  for (const key of keys) {
    const value = Number(row[key])
    if (Number.isFinite(value)) return value
  }
  return 0
}

// --- Governed server A/R + GL recipes ----------------------------------------
//
// Like the statement recipes above, the A/R and GL-transaction recipes were
// local-DuckDB-only. Their governed server contracts carry the same evidence:
// the A/R analysis artifact (`getQuickBooksArAnalysis`) has per-period ending
// A/R / DSO / aging plus ranked customer concerns, and the compact GL summary
// (`getQuickBooksGlSummary`) serves bounded transaction-level rows filtered by
// month + account text. These builders serve the identical recipe result shape
// from the server when the local path is unavailable or not warmed.

const arTrendPeriodMonth = (period: QuickBooksArTrendPeriod | null | undefined): string =>
  normalizeMonthStart(period?.periodStart ?? period?.period)

const buildArCollectabilityServerRecipe = async (
  appApi: CfoDrilldownServerContext['appApi'],
  workbookId: string,
  recipe: CfoDrilldownRecipeManifest,
  args: Record<string, any>,
  asOfHint: string | null,
): Promise<CfoDrilldownRecipeResult> => {
  const comparisonKind = requestedCfoDrilldownComparisonKind(args, 'mom')
  const window = buildCfoDrilldownWindow(normalizeMonthStart(args.as_of ?? args.asOf ?? asOfHint), comparisonKind)
  const requestedAsOf = window?.currentStart || normalizeMonthStart(args.as_of ?? args.asOf ?? asOfHint) || undefined
  const response: ArAnalysisResponse = await appApi.getQuickBooksArAnalysis!(
    workbookId,
    requestedAsOf ? { asOf: requestedAsOf, grain: 'month' } : { grain: 'month' },
  )
  const analysis = response.ar_analysis || null
  const periods = (analysis?.periods || []).slice().sort((a, b) => arTrendPeriodMonth(a).localeCompare(arTrendPeriodMonth(b)))
  const current =
    (window ? periods.find((p) => arTrendPeriodMonth(p) === window.currentStart) : null) ||
    periods[periods.length - 1] ||
    null
  const prior = window ? periods.find((p) => arTrendPeriodMonth(p) === window.priorStart) || null : null
  const currentAr = num(current?.endingAr, num(response.receivable_total))
  // The top-level receivable_prior/dso_prior are prior-MONTH values; only use
  // them when the requested comparison is in fact month-over-month.
  const priorAr = prior ? num(prior.endingAr) : comparisonKind === 'mom' ? num(response.receivable_prior) : 0
  const delta = currentAr - priorAr
  const dso = num(current?.dso, num(response.dso))
  const priorDso = prior ? num(prior.dso) : comparisonKind === 'mom' ? num(response.dso_prior) : 0
  const currentMonth = arTrendPeriodMonth(current) || window?.currentStart || trimStr(response.as_of)
  const customerRows = (analysis?.topCustomerConcerns || [])
    .map((concern) => ({
      customer_id: trimStr(concern.customerId),
      customer_name: trimStr(concern.customer) || 'Customer',
      period: currentMonth,
      ending_ar: num(concern.endingAr),
      over_90_ar: num(concern.over90Ar),
      dso: num(concern.dso),
    }))
    .sort((a, b) => Math.abs(b.ending_ar) - Math.abs(a.ending_ar))
    .slice(0, 12)
  if (!current && !customerRows.length) {
    return serverStatementsNotReady(
      recipe,
      'The governed A/R analysis has no periods or customer detail for the requested window yet.',
      'server_ar_analysis',
    )
  }
  const windowLabel = window?.label || 'vs. prior month'
  const windowShortLabel = window?.shortLabel || 'MoM'
  return {
    status: 'ready',
    recipe_id: recipe.id,
    recipe_label: recipe.label,
    manifest_version: CFO_DRILLDOWN_MANIFEST.version,
    view_kind: customerRows.length > 12 ? 'table' : 'bar',
    metric_id: normalizeCfoDrilldownMetricId(args.metric_id ?? args.metricId ?? 'accounts_receivable') || 'accounts_receivable',
    metric_label: trimStr(args.metric_label ?? args.metricLabel) || 'Accounts Receivable',
    comparison: comparisonKind,
    comparison_label: windowLabel,
    as_of: currentMonth,
    current_window: window ? { start: window.currentStart, end_exclusive: window.currentEndExclusive } : undefined,
    prior_window: window ? { start: window.priorStart, end_exclusive: window.priorEndExclusive } : undefined,
    delta_abs: delta,
    net_income_effect: null,
    summary: `A/R changed ${delta >= 0 ? '+' : '-'}${Math.round(Math.abs(delta))} ${windowShortLabel}; DSO moved ${dso - priorDso >= 0 ? '+' : '-'}${Math.round(Math.abs(dso - priorDso))} days.`,
    customer_rows: customerRows,
    chart_specs: [
      {
        kind: 'bar',
        title: 'A/R exposure by customer',
        subtitle: `${windowShortLabel}: selected current period only.`,
        items: customerRows.slice(0, 8).map((row) => ({
          label: row.customer_name,
          value: row.ending_ar,
          direction: row.ending_ar > 0 ? 'down' : row.ending_ar < 0 ? 'up' : 'neutral',
          note: row.over_90_ar ? `Over 90: ${Math.round(row.over_90_ar)}` : null,
          prompt: `Show invoices driving ${row.customer_name} A/R.`,
        })),
        rows: customerRows,
        columns: [
          { key: 'customer_name', label: 'Customer' },
          { key: 'ending_ar', label: 'Ending A/R', align: 'right' },
          { key: 'over_90_ar', label: 'Over 90', align: 'right' },
          { key: 'dso', label: 'DSO', align: 'right' },
        ],
      },
      {
        kind: 'table',
        title: 'A/R period movement',
        subtitle: `${windowShortLabel}: selected comparison only.`,
        rows: [
          { item: 'Prior window', amount: priorAr },
          { item: 'Current window', amount: currentAr },
          { item: 'A/R change', amount: delta },
          { item: 'DSO change', amount: dso - priorDso },
        ],
        columns: [
          { key: 'item', label: 'Item' },
          { key: 'amount', label: 'Amount', align: 'right' },
        ],
      },
    ],
    actions: [
      { label: 'Inspect customers', prompt: 'Which customers drove A/R movement?' },
      { label: 'Check invoices', prompt: 'Show invoices driving the largest A/R customer movement.' },
    ],
    freshness: { source: 'server_ar_analysis' },
    fallback_policy: { custom_sql_allowed: false, custom_sql_scope: 'manifest_views_only' },
    telemetry: {
      selected_metric_id: 'accounts_receivable',
      source_component: trimStr(args.source_component ?? args.sourceComponent),
      selected_recipe_id: recipe.id,
      readiness_status: 'ready',
      row_count: customerRows.length,
      render_kind: customerRows.length > 12 ? 'table' : 'bar',
      fallback_used: true,
    },
  }
}

const glSummaryRowValue = (row: Record<string, string>, keys: string[]): string => {
  for (const key of keys) {
    const value = trimStr(row[key])
    if (value) return value
  }
  return ''
}

/** Months covered by [start, endExclusive), oldest first, capped for bounded fan-out. */
const monthsInWindow = (start: string, endExclusive: string, cap: number): string[] => {
  const out: string[] = []
  for (let month = normalizeMonthStart(start); month && month < endExclusive; month = addMonths(month, 1)) {
    out.push(month)
    if (out.length >= 24) break
  }
  return out.slice(-cap)
}

const buildGlTransactionServerRecipe = async (
  appApi: CfoDrilldownServerContext['appApi'],
  workbookId: string,
  recipe: CfoDrilldownRecipeManifest,
  args: Record<string, any>,
  asOfHint: string | null,
): Promise<CfoDrilldownRecipeResult> => {
  const comparisonKind = requestedCfoDrilldownComparisonKind(args, 'mom')
  const window = buildCfoDrilldownWindow(normalizeMonthStart(args.as_of ?? args.asOf ?? asOfHint), comparisonKind)
  if (!window) {
    return serverStatementsNotReady(recipe, 'GL transaction drilldown could not determine a selected comparison window.', 'server_gl_summary')
  }
  // The compact GL summary filters by text (account label / breadcrumb / memo),
  // not by QBO account id — raw GL rows don't carry layout node ids. Prefer an
  // explicit account label from the prior drilldown layer.
  const accountLabels = [
    ...toStringArray(args.account_label ?? args.accountLabel ?? args.account_labels ?? args.accountLabels),
    ...toStringArray(args.account_name ?? args.accountName),
    ...toStringArray(args.selection?.account_label ?? args.selection?.accountLabel),
    ...toStringArray(args.search ?? args.query),
  ].filter(Boolean)
  if (!accountLabels.length) {
    return serverStatementsNotReady(
      recipe,
      'Transaction-level GL detail needs the selected account label (account_label) from the prior drilldown layer to query the governed GL summary.',
      'server_gl_summary',
    )
  }
  const search = accountLabels[0]
  // One bounded call per covered month (mom = 1); wider windows read the most
  // recent months so a qoq/yoy click still returns current-window evidence.
  const months = monthsInWindow(window.currentStart, window.currentEndExclusive, 3)
  const responses: QuickBooksGlSummaryResponse[] = await Promise.all(
    months.map((month) => appApi.getQuickBooksGlSummary!(workbookId, { asOf: month, search, limit: 80 })),
  )
  const transactionRows = responses
    .flatMap((response) => (Array.isArray(response.rows) ? response.rows : []))
    .map((row) => ({
      txn_date: glSummaryRowValue(row, ['txn_date', 'date']),
      account_id: trimStr(toStringArray(args.account_id ?? args.accountId ?? args.account_ids ?? args.accountIds)[0]),
      account_label: glSummaryRowValue(row, ['account_name', 'account']) || search,
      vendor_name: glSummaryRowValue(row, ['name', 'vendor_name', 'customer_name']) || 'Unspecified',
      memo: glSummaryRowValue(row, ['memo']),
      transaction_type: glSummaryRowValue(row, ['transaction_type']),
      document_number: glSummaryRowValue(row, ['num']),
      amount: num(row.amount, num(row.debit) - num(row.credit)),
    }))
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
    .slice(0, 80)
  const limited = responses.some((response) => Boolean(response.limited))
  return {
    status: 'ready',
    recipe_id: recipe.id,
    recipe_label: recipe.label,
    manifest_version: CFO_DRILLDOWN_MANIFEST.version,
    view_kind: 'table',
    metric_id: normalizeCfoDrilldownMetricId(args.metric_id ?? args.metricId ?? 'transaction_detail'),
    metric_label: trimStr(args.metric_label ?? args.metricLabel) || 'Transaction detail',
    comparison: comparisonKind,
    comparison_label: window.label,
    as_of: window.currentStart,
    current_window: { start: window.currentStart, end_exclusive: window.currentEndExclusive },
    prior_window: { start: window.priorStart, end_exclusive: window.priorEndExclusive },
    delta_abs: transactionRows.reduce((sum, row) => sum + row.amount, 0),
    net_income_effect: null,
    summary:
      `Governed GL summary returned ${transactionRows.length} transaction(s) matching "${search}" in the current ${window.shortLabel} window` +
      (limited ? ' (bounded to the largest rows per month).' : '.'),
    transaction_rows: transactionRows,
    chart_specs: [
      {
        kind: 'table',
        title: 'Transaction-level GL detail',
        subtitle: 'Largest current-window transactions for the selected account.',
        rows: transactionRows.slice(0, 20),
        columns: [
          { key: 'txn_date', label: 'Date' },
          { key: 'vendor_name', label: 'Vendor/name' },
          { key: 'memo', label: 'Memo' },
          { key: 'transaction_type', label: 'Type' },
          { key: 'amount', label: 'Amount', align: 'right' },
        ],
      },
    ],
    actions: [
      { label: 'Group by vendor', prompt: 'Group these transactions by vendor/name.' },
      { label: 'Find unusual items', prompt: 'Which transactions look unusual for this account?' },
    ],
    freshness: { source: 'server_gl_summary' },
    fallback_policy: { custom_sql_allowed: false, custom_sql_scope: 'manifest_views_only' },
    telemetry: {
      selected_metric_id: trimStr(args.metric_id ?? args.metricId),
      source_component: trimStr(args.source_component ?? args.sourceComponent),
      selected_recipe_id: recipe.id,
      readiness_status: 'ready',
      row_count: transactionRows.length,
      render_kind: 'table',
      fallback_used: true,
    },
  }
}

const buildArCollectabilityRecipe = async (
  workerClient: AppWorkerClient,
  duckDbOpts: Record<string, any> | null | undefined,
  recipe: CfoDrilldownRecipeManifest,
  args: Record<string, any>,
  asOfHint: string | null,
  artifactReadiness: CfoDrilldownArtifactReadiness[],
): Promise<CfoDrilldownRecipeResult> => {
  const comparisonKind = requestedCfoDrilldownComparisonKind(args, 'mom')
  const window = buildCfoDrilldownWindow(normalizeMonthStart(args.as_of ?? args.asOf ?? asOfHint), comparisonKind)
  if (!window) return recipeNotReady(recipe, 'A/R drilldown could not determine a selected comparison window from local artifacts.', [], artifactReadiness)
  const table = CFO_DRILLDOWN_VIEWS_BY_ID.get('ar_analysis')?.table || 'fg_qbo_fpa_ar_analysis_table'
  const rows = await runDuckRows(
    workerClient,
    `
SELECT section, as_of_date, payload_json
FROM ${quoteDuckIdent(table)}
WHERE section IN ('periods', 'customer_period_trends', 'top_customer_concerns', 'collectability')
ORDER BY as_of_date, ordinal
LIMIT 2000`,
    { previewRowLimit: 2000, timeoutMs: 90000, caller: `CfoDrilldownRecipes:run_drilldown_recipe:${recipe.id}` },
    duckDbOpts,
  )
  const payloads = rows.map((row) => ({
    section: trimStr(row.section),
    as_of_date: normalizeMonthStart(row.as_of_date),
    payload: parseJsonPayload(row.payload_json),
  }))
  const periodRows = payloads
    .filter((row) => row.section === 'periods')
    .map((row) => ({ ...row.payload, period: normalizeMonthStart(row.payload.period ?? row.payload.periodStart ?? row.as_of_date) }))
    .filter((row) => row.period)
    .sort((a, b) => rowPeriod(a).localeCompare(rowPeriod(b)))
  const current = periodRows.find((row) => rowPeriod(row) === window.currentStart) || periodRows[periodRows.length - 1] || null
  const prior = periodRows.find((row) => rowPeriod(row) === window.priorStart) || null
  const currentAr = payloadNumber(current || {}, ['endingAr', 'endingAR', 'totalAr', 'accountsReceivable', 'currentExposure'])
  const priorAr = payloadNumber(prior || {}, ['endingAr', 'endingAR', 'totalAr', 'accountsReceivable', 'currentExposure'])
  const delta = currentAr - priorAr
  const dso = payloadNumber(current || {}, ['dso', 'daysSalesOutstanding'])
  const priorDso = payloadNumber(prior || {}, ['dso', 'daysSalesOutstanding'])
  const customerRows = payloads
    .filter((row) => row.section === 'customer_period_trends')
    .map((row) => ({
      customer_id: trimStr(row.payload.customerId),
      customer_name: trimStr(row.payload.customerName ?? row.payload.name ?? row.payload.customerId) || 'Customer',
      period: normalizeMonthStart(row.payload.period ?? row.payload.periodStart ?? row.as_of_date),
      ending_ar: payloadNumber(row.payload, ['endingAr', 'endingAR', 'totalAr', 'currentExposure']),
      over_90_ar: payloadNumber(row.payload, ['over90Ar', 'over90AR', 'over90']),
      dso: payloadNumber(row.payload, ['dso', 'daysSalesOutstanding']),
    }))
    .filter((row) => row.period === (current?.period || window.currentStart))
    .sort((a, b) => Math.abs(b.ending_ar) - Math.abs(a.ending_ar))
    .slice(0, 12)
  return {
    status: 'ready',
    recipe_id: recipe.id,
    recipe_label: recipe.label,
    manifest_version: CFO_DRILLDOWN_MANIFEST.version,
    view_kind: customerRows.length > 12 ? 'table' : 'bar',
    metric_id: normalizeCfoDrilldownMetricId(args.metric_id ?? args.metricId ?? 'accounts_receivable') || 'accounts_receivable',
    metric_label: trimStr(args.metric_label ?? args.metricLabel) || 'Accounts Receivable',
    comparison: comparisonKind,
    comparison_label: window.label,
    as_of: current?.period || window.currentStart,
    current_window: { start: window.currentStart, end_exclusive: window.currentEndExclusive },
    prior_window: { start: window.priorStart, end_exclusive: window.priorEndExclusive },
    delta_abs: delta,
    net_income_effect: null,
    summary: `A/R changed ${delta >= 0 ? '+' : '-'}${Math.round(Math.abs(delta))} ${window.shortLabel}; DSO moved ${dso - priorDso >= 0 ? '+' : '-'}${Math.round(Math.abs(dso - priorDso))} days.`,
    customer_rows: customerRows,
    chart_specs: [
      {
        kind: 'bar',
        title: 'A/R exposure by customer',
        subtitle: `${window.shortLabel}: selected current period only.`,
        items: customerRows.slice(0, 8).map((row) => ({
          label: row.customer_name,
          value: row.ending_ar,
          direction: row.ending_ar > 0 ? 'down' : row.ending_ar < 0 ? 'up' : 'neutral',
          note: row.over_90_ar ? `Over 90: ${Math.round(row.over_90_ar)}` : null,
          prompt: `Show invoices driving ${row.customer_name} A/R.`,
        })),
        rows: customerRows,
        columns: [
          { key: 'customer_name', label: 'Customer' },
          { key: 'ending_ar', label: 'Ending A/R', align: 'right' },
          { key: 'over_90_ar', label: 'Over 90', align: 'right' },
          { key: 'dso', label: 'DSO', align: 'right' },
        ],
      },
      {
        kind: 'table',
        title: 'A/R period movement',
        subtitle: `${window.shortLabel}: selected comparison only.`,
        rows: [
          { item: 'Prior window', amount: priorAr },
          { item: 'Current window', amount: currentAr },
          { item: 'A/R change', amount: delta },
          { item: 'DSO change', amount: dso - priorDso },
        ],
        columns: [
          { key: 'item', label: 'Item' },
          { key: 'amount', label: 'Amount', align: 'right' },
        ],
      },
    ],
    actions: [
      { label: 'Inspect customers', prompt: 'Which customers drove A/R movement?' },
      { label: 'Check invoices', prompt: 'Show invoices driving the largest A/R customer movement.' },
    ],
    freshness: { source: 'local_duckdb', tables: [table], artifact_readiness: artifactReadiness },
    fallback_policy: { custom_sql_allowed: Boolean(recipe.supports_custom_sql_fallback), custom_sql_scope: 'manifest_views_only' },
    telemetry: {
      selected_metric_id: 'accounts_receivable',
      source_component: trimStr(args.source_component ?? args.sourceComponent),
      selected_recipe_id: recipe.id,
      readiness_status: 'ready',
      row_count: customerRows.length,
      render_kind: customerRows.length > 12 ? 'table' : 'bar',
      fallback_used: false,
    },
  }
}

const rowPeriod = (row: Record<string, any>): string => normalizeMonthStart(row.period ?? row.periodStart ?? row.as_of_date)

// Segment / service-line contribution movement. Unlike the other recipes, this
// is NOT computed from local DuckDB — the service-line rollup definitions
// (which layout nodes pair as revenue vs. direct COGS) live server-side and are
// already computed into `direct_cm_<rollup>` ratio cards by the governed ratios
// contract (components: revenue/cogs/contribution $ at the latest period; a
// component_trends series for revenue/cogs; MoM/QoQ/etc comparisons on the CM%
// itself). Reuse that contract rather than re-deriving the node-id grouping
// client-side, which would risk drifting from the governed calculation.
const buildSegmentContributionRecipe = async (
  appApi: Pick<ServerApiClient, 'getQuickBooksRatios'>,
  workbookId: string | null | undefined,
  recipe: CfoDrilldownRecipeManifest,
  args: Record<string, any>,
): Promise<CfoDrilldownRecipeResult> => {
  const wb = trimStr(workbookId)
  if (!wb) {
    return {
      status: 'not_ready',
      recipe_id: recipe.id,
      recipe_label: recipe.label,
      manifest_version: CFO_DRILLDOWN_MANIFEST.version,
      summary: 'Segment contribution movement needs an active workbook.',
      freshness: { source: 'server_ratios' },
    }
  }
  const asOf = trimStr(args.as_of ?? args.asOf) || undefined
  let response
  try {
    response = await appApi.getQuickBooksRatios(wb, { asOf, grain: 'month' })
  } catch (err) {
    return {
      status: 'not_ready',
      recipe_id: recipe.id,
      recipe_label: recipe.label,
      manifest_version: CFO_DRILLDOWN_MANIFEST.version,
      summary: `Segment contribution movement could not be loaded: ${(err as Error)?.message || 'request failed'}.`,
      freshness: { source: 'server_ratios' },
    }
  }
  const cards = response.categories.flatMap((category) => category.ratios).filter((card) => card.id.startsWith('direct_cm_'))
  if (!cards.length) {
    return {
      status: 'not_ready',
      recipe_id: recipe.id,
      recipe_label: recipe.label,
      manifest_version: CFO_DRILLDOWN_MANIFEST.version,
      as_of: response.as_of,
      summary: 'No service-line/segment rollups are configured for this workbook, so segment contribution detail is not available.',
      semantic_gap: {
        required: [],
        message: 'Configure service-line rollups (Financials -> gear -> Detect lines) to unlock per-segment contribution margin movement.',
      },
      freshness: { source: 'server_ratios' },
      fallback_policy: { custom_sql_allowed: false, custom_sql_scope: 'manifest_views_only' },
    }
  }

  const componentValue = (card: RatioCard, suffix: string): number | null =>
    card.components.find((c) => c.id === `${card.id}_${suffix}`)?.value ?? null
  const seriesEdge = (card: RatioCard, suffix: string): { current: number | null; prior: number | null } => {
    const trend = (card.component_trends || []).find((s) => s.id === `${card.id}_${suffix}_series`)?.trend || []
    if (trend.length < 2) return { current: trend[trend.length - 1]?.value ?? null, prior: null }
    return { current: trend[trend.length - 1].value, prior: trend[trend.length - 2].value }
  }
  const momOf = (card: RatioCard) => card.comparisons.find((c) => c.kind === 'mom') || null

  const rows = cards
    .map((card) => {
      const name = card.label.replace(/\s*—\s*direct contribution margin$/i, '').trim() || card.label
      const revenue = seriesEdge(card, 'revenue')
      const cogs = seriesEdge(card, 'cogs')
      const revenueDelta = revenue.current != null && revenue.prior != null ? revenue.current - revenue.prior : null
      const cogsDelta = cogs.current != null && cogs.prior != null ? cogs.current - cogs.prior : null
      const contributionDelta = revenueDelta != null ? revenueDelta - (cogsDelta ?? 0) : null
      const mom = momOf(card)
      return {
        rollup_id: card.id.replace(/^direct_cm_/, ''),
        name,
        revenue_current: componentValue(card, 'revenue'),
        revenue_prior: revenue.prior,
        revenue_delta_abs: revenueDelta,
        cogs_current: componentValue(card, 'cogs'),
        cogs_prior: cogs.prior,
        cogs_delta_abs: cogsDelta,
        contribution_current: componentValue(card, 'contribution'),
        contribution_prior: revenue.prior != null ? revenue.prior - (cogs.prior ?? 0) : null,
        contribution_delta_abs: contributionDelta,
        cm_pct_current: card.latest,
        cm_pct_prior: mom?.prior ?? null,
        cm_pct_delta_abs: mom?.delta_abs ?? null,
      }
    })
    .sort((a, b) => Math.abs(b.contribution_delta_abs ?? 0) - Math.abs(a.contribution_delta_abs ?? 0))

  const totalContributionDelta = rows.reduce((sum, row) => sum + (row.contribution_delta_abs ?? 0), 0)
  const top = rows[0]
  const summary = top
    ? `${rows.length} segment(s) configured. ${top.name} drove the largest direct-contribution movement (${top.contribution_delta_abs != null ? `${top.contribution_delta_abs >= 0 ? '+' : ''}${Math.round(top.contribution_delta_abs)}` : 'n/a'}), direct CM ${top.cm_pct_current != null ? `${Math.round(top.cm_pct_current * 100)}%` : 'n/a'} of its revenue this period.`
    : `${rows.length} segment(s) configured, but no clean month-over-month movement could be computed yet.`

  return {
    status: 'ready',
    recipe_id: recipe.id,
    recipe_label: recipe.label,
    manifest_version: CFO_DRILLDOWN_MANIFEST.version,
    view_kind: 'table',
    metric_id: 'revenue',
    metric_label: 'Revenue',
    comparison: 'mom',
    comparison_label: 'Month over month',
    as_of: response.as_of,
    delta_abs: totalContributionDelta,
    net_income_effect: totalContributionDelta,
    summary,
    category_rows: rows,
    chart_specs: [
      {
        kind: 'bar',
        title: 'Segment contribution movement (MoM)',
        subtitle: 'Direct contribution-dollar change by service line / segment.',
        items: rows.slice(0, 8).map((row) => ({
          label: row.name,
          value: row.contribution_delta_abs ?? 0,
          direction: (row.contribution_delta_abs ?? 0) >= 0 ? 'up' : 'down',
          note: row.cm_pct_current != null ? `CM ${Math.round(row.cm_pct_current * 100)}%` : null,
          prompt: `Break down ${row.name} revenue and COGS movement.`,
        })),
        rows,
        columns: [
          { key: 'name', label: 'Segment' },
          { key: 'revenue_current', label: 'Revenue', align: 'right' },
          { key: 'cogs_current', label: 'Direct COGS', align: 'right' },
          { key: 'contribution_current', label: 'Contribution', align: 'right' },
          { key: 'contribution_delta_abs', label: 'MoM Δ', align: 'right' },
          { key: 'cm_pct_current', label: 'CM %', align: 'right' },
        ],
      },
    ],
    actions: rows.slice(0, 2).map((row) => ({
      label: `Break down ${row.name}`,
      prompt: `Break down ${row.name} revenue and COGS movement vs. prior month.`,
    })),
    freshness: { source: 'server_ratios' },
    fallback_policy: { custom_sql_allowed: false, custom_sql_scope: 'manifest_views_only' },
    telemetry: {
      selected_metric_id: 'revenue',
      selected_recipe_id: recipe.id,
      readiness_status: 'ready',
      row_count: rows.length,
      render_kind: 'table',
      fallback_used: false,
    },
  }
}

const buildGlTransactionRecipe = async (
  workerClient: AppWorkerClient,
  duckDbOpts: Record<string, any> | null | undefined,
  recipe: CfoDrilldownRecipeManifest,
  args: Record<string, any>,
  asOfHint: string | null,
  artifactReadiness: CfoDrilldownArtifactReadiness[],
): Promise<CfoDrilldownRecipeResult> => {
  const comparisonKind = requestedCfoDrilldownComparisonKind(args, 'mom')
  const window = buildCfoDrilldownWindow(normalizeMonthStart(args.as_of ?? args.asOf ?? asOfHint), comparisonKind)
  if (!window) return recipeNotReady(recipe, 'GL transaction drilldown could not determine a selected comparison window from local artifacts.', [], artifactReadiness)
  const accountIds = [
    ...toStringArray(args.account_id ?? args.accountId ?? args.account_ids ?? args.accountIds),
    ...toStringArray(args.selection?.account_id ?? args.selection?.accountId ?? args.selection?.account_ids ?? args.selection?.accountIds),
  ].filter(Boolean)
  if (!accountIds.length) {
    return recipeNotReady(recipe, 'Transaction-level GL detail needs a selected account from the prior drilldown layer.', [], artifactReadiness)
  }
  const table = CFO_DRILLDOWN_VIEWS_BY_ID.get('gl_detail')?.table || 'fg_cfo_ai_gl_detail'
  const accountIn = accountIds.slice(0, 10).map(quoteDuckString).join(', ')
  const rows = await runDuckRows(
    workerClient,
    `
SELECT
  CAST(TRY_CAST(txn_date AS DATE) AS VARCHAR) AS txn_date,
  account_id,
  account_label,
  nullif(trim(coalesce(vendor, '')), '') AS vendor,
  nullif(trim(coalesce(customer, '')), '') AS customer,
  trim(coalesce(memo, '')) AS memo,
  trim(coalesce(transaction_type, '')) AS transaction_type,
  trim(coalesce(document_number, '')) AS document_number,
  coalesce(TRY_CAST(amount AS DOUBLE), 0) AS amount
FROM ${quoteDuckIdent(table)}
WHERE account_id IN (${accountIn})
  AND TRY_CAST(txn_date AS DATE) >= TRY_CAST(${quoteDuckString(window.currentStart)} AS DATE)
  AND TRY_CAST(txn_date AS DATE) < TRY_CAST(${quoteDuckString(window.currentEndExclusive)} AS DATE)
ORDER BY abs(coalesce(TRY_CAST(amount AS DOUBLE), 0)) DESC
LIMIT 80`,
    { previewRowLimit: 100, timeoutMs: 120000, caller: `CfoDrilldownRecipes:run_drilldown_recipe:${recipe.id}` },
    duckDbOpts,
  )
  const transactionRows = rows.map((row) => ({
    txn_date: trimStr(row.txn_date),
    account_id: trimStr(row.account_id),
    account_label: trimStr(row.account_label),
    vendor_name: trimStr(row.vendor || row.customer) || 'Unspecified',
    memo: trimStr(row.memo),
    transaction_type: trimStr(row.transaction_type),
    document_number: trimStr(row.document_number),
    amount: num(row.amount),
  }))
  return {
    status: 'ready',
    recipe_id: recipe.id,
    recipe_label: recipe.label,
    manifest_version: CFO_DRILLDOWN_MANIFEST.version,
    view_kind: 'table',
    metric_id: normalizeCfoDrilldownMetricId(args.metric_id ?? args.metricId ?? 'transaction_detail'),
    metric_label: trimStr(args.metric_label ?? args.metricLabel) || 'Transaction detail',
    comparison: comparisonKind,
    comparison_label: window.label,
    as_of: window.currentStart,
    current_window: { start: window.currentStart, end_exclusive: window.currentEndExclusive },
    prior_window: { start: window.priorStart, end_exclusive: window.priorEndExclusive },
    delta_abs: transactionRows.reduce((sum, row) => sum + row.amount, 0),
    net_income_effect: null,
    summary: `GL detail returned ${transactionRows.length} transaction(s) for the selected account in the current ${window.shortLabel} window.`,
    transaction_rows: transactionRows,
    chart_specs: [
      {
        kind: 'table',
        title: 'Transaction-level GL detail',
        subtitle: 'Largest current-window transactions for the selected account.',
        rows: transactionRows.slice(0, 20),
        columns: [
          { key: 'txn_date', label: 'Date' },
          { key: 'vendor_name', label: 'Vendor/name' },
          { key: 'memo', label: 'Memo' },
          { key: 'transaction_type', label: 'Type' },
          { key: 'amount', label: 'Amount', align: 'right' },
        ],
      },
    ],
    actions: [
      { label: 'Group by vendor', prompt: 'Group these transactions by vendor/name.' },
      { label: 'Find unusual items', prompt: 'Which transactions look unusual for this account?' },
    ],
    freshness: { source: 'local_duckdb', tables: [table], artifact_readiness: artifactReadiness },
    fallback_policy: { custom_sql_allowed: Boolean(recipe.supports_custom_sql_fallback), custom_sql_scope: 'manifest_views_only' },
    telemetry: {
      selected_metric_id: trimStr(args.metric_id ?? args.metricId),
      source_component: trimStr(args.source_component ?? args.sourceComponent),
      selected_recipe_id: recipe.id,
      readiness_status: 'ready',
      row_count: transactionRows.length,
      render_kind: 'table',
      fallback_used: false,
    },
  }
}

export const runCfoDrilldownRecipe = async (
  workerClient: AppWorkerClient | null | undefined,
  duckDbOpts: Record<string, any> | null | undefined,
  asOfHint: string | null,
  args: Record<string, any>,
  serverCtx?: CfoDrilldownServerContext,
): Promise<CfoDrilldownRecipeResult> => {
  const inferredRecipe = findCfoDrilldownRecipeForMetric(
    args.metric_id ?? args.metricId ?? args.metric ?? args.selected_metric_id,
    args.source_component ?? args.sourceComponent,
  )
  const recipeId = trimStr(args.recipe_id ?? args.recipeId) || inferredRecipe?.id || ''
  const recipe = CFO_DRILLDOWN_RECIPES_BY_ID.get(recipeId) || null
  if (!recipe) {
    return {
      status: 'not_ready',
      recipe_id: recipeId || null,
      manifest_version: CFO_DRILLDOWN_MANIFEST.version,
      summary: `No CFO drilldown recipe is registered for "${recipeId || trimStr(args.metric_id ?? args.metricId) || 'the selected metric'}".`,
      fallback_policy: { custom_sql_allowed: true, custom_sql_scope: 'manifest_views_only', label: 'Ad hoc manifest-view SQL' },
      freshness: { source: 'local_duckdb' },
    }
  }
  // Server-fetched recipe: the underlying data (service-line rollup config)
  // lives in Postgres, not local DuckDB, so this bypasses the worker gate below.
  if (recipe.id === 'segment_contribution_movement') {
    if (!serverCtx?.appApi) {
      return {
        status: 'not_ready',
        recipe_id: recipe.id,
        recipe_label: recipe.label,
        manifest_version: CFO_DRILLDOWN_MANIFEST.version,
        summary: 'Segment contribution movement requires the server ratio contract, which is not available in this context.',
        freshness: { source: 'server_ratios' },
      }
    }
    return await buildSegmentContributionRecipe(serverCtx.appApi, serverCtx.workbookId, recipe, args)
  }
  // Statement recipes have a governed server implementation. Serve from it
  // whenever the local path can't run: lightweight CFO pages never boot the
  // app worker, and the local semantic views require an explicit warmup.
  const serverWorkbookId = trimStr(serverCtx?.workbookId)
  const canServeFromStatements =
    SERVER_STATEMENT_RECIPE_IDS.has(recipe.id) &&
    typeof serverCtx?.appApi?.getQuickBooksCfoStatementsRender === 'function' &&
    Boolean(serverWorkbookId)
  const runServerStatementRecipe = async (): Promise<CfoDrilldownRecipeResult> => {
    try {
      return recipe.id === 'pnl_major_driver_decomposition'
        ? await buildPnlDriverServerRecipe(serverCtx!.appApi, serverWorkbookId, recipe, args, asOfHint)
        : await buildStatementLineServerRecipe(serverCtx!.appApi, serverWorkbookId, recipe, args, asOfHint)
    } catch (err) {
      return serverStatementsNotReady(recipe, `${recipe.label} could not be computed from the governed statements render: ${(err as Error)?.message || 'unexpected error'}.`)
    }
  }
  // A/R + GL-transaction recipes have their own governed server contracts.
  const canServeArFromServer =
    recipe.id === 'ar_collectability_summary' &&
    typeof serverCtx?.appApi?.getQuickBooksArAnalysis === 'function' &&
    Boolean(serverWorkbookId)
  const canServeGlFromServer =
    recipe.id === 'gl_transaction_detail' &&
    typeof serverCtx?.appApi?.getQuickBooksGlSummary === 'function' &&
    Boolean(serverWorkbookId)
  const runServerFallbackRecipe = async (): Promise<CfoDrilldownRecipeResult> => {
    if (canServeFromStatements) return await runServerStatementRecipe()
    const source = canServeArFromServer ? 'server_ar_analysis' : 'server_gl_summary'
    try {
      return canServeArFromServer
        ? await buildArCollectabilityServerRecipe(serverCtx!.appApi, serverWorkbookId, recipe, args, asOfHint)
        : await buildGlTransactionServerRecipe(serverCtx!.appApi, serverWorkbookId, recipe, args, asOfHint)
    } catch (err) {
      return serverStatementsNotReady(recipe, `${recipe.label} could not be computed from the governed server contract: ${(err as Error)?.message || 'unexpected error'}.`, source)
    }
  }
  const canServeFromServer = canServeFromStatements || canServeArFromServer || canServeGlFromServer
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') {
    if (canServeFromServer) return await runServerFallbackRecipe()
    return recipeNotReady(recipe, 'Local DuckDB is not available yet, so CFO drilldown evidence cannot be computed.', requiredCfoDrilldownTablesForRecipe(recipe))
  }
  const { missing, artifactReadiness } = await ensureRecipeTablesReady(workerClient, duckDbOpts, recipe)
  if (missing.length) {
    if (canServeFromServer) return await runServerFallbackRecipe()
    return recipeNotReady(recipe, `${recipe.label} cannot run because required CFO semantic view(s) are not ready yet.`, missing, artifactReadiness)
  }
  if (recipe.id === 'pnl_major_driver_decomposition') {
    return await buildPnlDriverRecipe(workerClient, duckDbOpts, recipe, args, asOfHint, artifactReadiness)
  }
  if (
    recipe.id === 'statement_line_account_movement' ||
    recipe.id === 'opex_category_account_movement' ||
    recipe.id === 'cash_account_movement'
  ) {
    const metricArgs = recipe.id === 'cash_account_movement'
      ? { ...args, metric_id: args.metric_id ?? args.metricId ?? 'cash' }
      : args
    return await buildStatementLineRecipe(workerClient, duckDbOpts, recipe, metricArgs, asOfHint, artifactReadiness)
  }
  if (recipe.id === 'ar_collectability_summary') {
    return await buildArCollectabilityRecipe(workerClient, duckDbOpts, recipe, args, asOfHint, artifactReadiness)
  }
  if (recipe.id === 'gl_transaction_detail') {
    return await buildGlTransactionRecipe(workerClient, duckDbOpts, recipe, args, asOfHint, artifactReadiness)
  }
  return recipeNotReady(
    recipe,
    `${recipe.label} is registered in the CFO drilldown manifest, but its executor is not implemented in this client yet.`,
    requiredCfoDrilldownTablesForRecipe(recipe),
    artifactReadiness,
  )
}
